import React, { useEffect, useState, useMemo } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  IconMessage,
  IconFirstAidKit,
  IconClipboardList,
  IconShieldCheck,
  IconUserStar,
  IconPhone,
  IconMail,
  IconEdit,
  IconUserX,
  IconLogout,
  IconPencil,
  IconMedicalCross,
  IconDog,
  IconCat,
  IconCopy,
  IconX,
  IconClipboardPlus,
  IconUser,
  IconUsers,
  IconChevronRight,
} from "@tabler/icons-react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { isChild, MemberType } from "@/types/member";
import MAvatar from "@/components/ui/atoms/MAvatar";
import MBadge from "@/components/ui/atoms/MBadge";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import MInputField from "@/components/ui/atoms/MInputField";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import MDocumentUploadOptions from "@/components/ui/molecules/MDocumentUploadOptions";
import ProfileTrackerCard from "@/components/ui/molecules/ProfileTrackerCard";
import EditNicknameDrawer from "@/components/ui/molecules/EditNicknameDrawer";
import memberPermissionService from "@/services/member-permissions.service";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import pillboxService from "@/services/pillbox.service";
import { labInsightsService } from "@/services/lab-insights.service";
import { remindersService } from "@/services/reminders.service";
import actionableInsightsService from "@/services/actionable-insights.service";
import {
  MaatraSummaryCard,
  LabTrendsCard,
  VitalsTrackerCard,
  RecordsHeroCard,
  PillboxCard,
  RemindersCard,
} from "./components/MemberDetailCards";
import MemberDetailSkeleton from "./components/MemberDetailSkeleton";
import MRevealSection from "@/components/ui/atoms/MRevealSection";
import { LabInsightsLanding } from "@/types/lab-insights";
import { PillboxItem } from "@/types/pillbox";
import { TrackingConfig, VitalsParameter } from "@/types/vitals";
import { ReminderTypeCounts } from "@/types/reminders";
import { useFamilyStats } from "@/hooks/useFamilyStats";
import { getMemberInfo } from "@/hooks/useMembers";
import { useDocument } from "@/hooks/useDocument";
import { getAgeFromDob } from "@/hooks/useMemberForm";
import { useFamily } from "@/hooks/useFamily";
import { setSelectedMember, clearSelectedMember } from "@/store/authSlice";
import { RootState } from "@/store";
import { ProfileStrength, MemberStats } from "@/types/familyStats";
import { membersService } from "@/services/members.service";
import { setNavigationSource } from "@/utils/navigationSource";
import { useScrollRestore } from "@/hooks/useScrollRestore";
import styles from "./MemberDetail.module.css";

interface FamilyMember {
  id?: string;
  _id?: string;
  first_name: string;
  last_name?: string;
  nickname?: string;
  registered_member: boolean;
  relationship?: string | number;
  avatar?: string;
  family_member_type?: string;
  dob_day?: number;
  dob_month?: number;
  dob_year?: number;
  weight?: number;
  invitation_status?: string;
  registration_status?: string;
  member_type?: string;
}

const MemberDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { memberId } = useParams<{ memberId: string }>();
  const memberInfo = getMemberInfo();
  const loggedInMember = useSelector((state: RootState) => state.auth.member);
  const { data: familyMembers = [] } = useFamily();
  const { familyStats, fetchFamilyStats } = useFamilyStats({ memberInfo });
  const [memberStats, setMemberStats] = useState<MemberStats | null>(null);
  // Lazy loaded - only fetched when ProfileTracker opens
  const [healthProfileSummary, setHealthProfileSummary] = useState<any>(null);
  const [vitals, setVitals] = useState<any>(null);
  const [vitalsTrackingReady, setVitalsTrackingReady] = useState(false);
  const [vitalsTrackingConfig, setVitalsTrackingConfig] =
    useState<TrackingConfig | null>(null);
  // Whether height grew since the previous reading — only meaningful (and
  // only fetched) for children, where growth is the point of tracking it;
  // adults' height doesn't change, so there's nothing useful to show.
  const [heightIncreased, setHeightIncreased] = useState(false);
  // Weight can trend either way for anyone (unlike height, not gated by age).
  const [weightTrend, setWeightTrend] = useState<"up" | "down" | null>(null);
  const [labLanding, setLabLanding] = useState<LabInsightsLanding | null>(null);
  const [pillboxItems, setPillboxItems] = useState<PillboxItem[] | null>(null);
  const [reminderCounts, setReminderCounts] =
    useState<ReminderTypeCounts | null>(null);
  const [recordCounts, setRecordCounts] = useState<any | null>(null);
  const [maatraSummary, setMaatraSummary] = useState<string | null>(null);
  const [maatraSummaryUpdatedAt, setMaatraSummaryUpdatedAt] = useState<
    string | null
  >(null);
  const [profileTrackerDataLoaded, setProfileTrackerDataLoaded] =
    useState(false);
  const [hasEmergencyContact, setHasEmergencyContact] = useState(false);
  const [member, setMember] = useState<FamilyMember | null>(null);
  const [managedMembers, setManagedMembers] = useState<any[]>([]);
  const [accessibleByMembers, setAccessibleByMembers] = useState<any[]>([]);
  const [showUploadOptions, setShowUploadOptions] = useState(false);
  const uploadOptionsRef = React.useRef(null);
  const [showHealthSummaryDialog, setShowHealthSummaryDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showProfileTracker, setShowProfileTracker] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [editNicknameMember, setEditNicknameMember] = useState<any | null>(
    null
  );
  const [editNicknameLoading, setEditNicknameLoading] = useState(false);
  const { showToast } = useToast();

  // Manage Invite dropdown (shown when member is in 'invited' status)
  const [manageInviteMenuOpen, setManageInviteMenuOpen] = React.useState(false);
  const manageInviteButtonRef = React.useRef<HTMLDivElement | null>(null);
  const manageInviteMenuRef = React.useRef<HTMLDivElement | null>(null);

  // Cancel Invite confirmation dialog
  const [showCancelInviteDialog, setShowCancelInviteDialog] =
    React.useState(false);
  const [cancelInviteLoading, setCancelInviteLoading] = React.useState(false);

  const { getDocumentTypeCounts, getAllDocuments } = useDocument(
    memberId || ""
  );
  const [documentTypeCounts, setDocumentTypeCounts] = useState<{
    prescription?: number;
    labReport?: number;
    dischargeSummary?: number;
    imagingReport?: number;
    insuranceCard?: number;
    medicalBill?: number;
  } | null>(null);
  const [documentYearSpan, setDocumentYearSpan] = useState<number | undefined>(
    undefined
  );

  // Get managedIds from useFamily hook
  const { managedIds } = useFamily();

  // Helper function to check if logged-in member can manage a specific member
  const canManageMember = (targetMemberId: string) => {
    if (!managedIds || !loggedInMember) return false;
    // managedIds is an array of member IDs that the logged-in user can manage
    // Check if targetMemberId is in the managedIds array
    return Array.isArray(managedIds) && managedIds.includes(targetMemberId);
  };

  // Compute registered members count for FO
  const registeredMembers = useMemo(
    () =>
      loggedInMember?.family_member_type === "fo"
        ? familyMembers.filter((m: any) => m.registered_member)
        : [],
    [familyMembers, loggedInMember]
  );

  // Set navigation source when visiting member detail page
  useEffect(() => {
    if (memberId) {
      setNavigationSource(
        "member-detail",
        `/profile/family/member/${memberId}`
      );
    }
  }, [memberId]);

  useEffect(() => {
    if (memberInfo?.id) {
      fetchFamilyStats();
    }
  }, [memberInfo?.current_family, fetchFamilyStats]);

  useEffect(() => {
    if (familyStats?.memberStats && memberId) {
      const stats = familyStats.memberStats[memberId];
      setMemberStats(stats || null);
    }
  }, [familyStats, memberId]);

  useEffect(() => {
    if (memberId && familyMembers.length > 0) {
      const foundMember = familyMembers.find(
        (m: FamilyMember) => (m.id || m._id) === memberId
      );
      setMember(foundMember || null);

      // Dispatch selectedMember to Redux and save to localStorage
      if (foundMember) {
        if (
          (foundMember.id || foundMember._id) ===
          (loggedInMember?.id || loggedInMember?._id)
        ) {
          // Clear selectedMember when viewing your own profile
          dispatch(clearSelectedMember());
        } else {
          // Set selectedMember when viewing another member's profile
          dispatch(setSelectedMember(foundMember));
        }
      }
    }
  }, [memberId, familyMembers, dispatch, loggedInMember]);

  useEffect(() => {
    // Fetch managed members (members this specific member can access)
    if (memberId) {
      membersService.getManagedMembersForMember(memberId).then((members) => {
        setManagedMembers(members || []);
      });
    }
  }, [memberId]);

  useEffect(() => {
    // Fetch members who can access this member's profile
    if (memberId) {
      membersService.getMembersWhoCanAccess(memberId).then((members) => {
        setAccessibleByMembers(members || []);
      });

      // Fetch vitals to get weight information
      import("@/services/vitals.service").then((VitalsServiceModule) => {
        VitalsServiceModule.default
          .getVitals(memberId)
          .then((vitalsData) => {
            setVitals(vitalsData);
          })
          .catch((err) => console.error("Failed to load vitals:", err));
      });

      // Fetch vitals tracking config to determine readiness
      vitalsTrackingService
        .getTrackingConfig(memberId)
        .then((config) => {
          setVitalsTrackingConfig(config);
          setVitalsTrackingReady(
            Array.isArray(config?.enabledParameters) &&
              config.enabledParameters.length > 0
          );
        })
        .catch(() => {
          setVitalsTrackingConfig(null);
          setVitalsTrackingReady(false);
        });
    }
  }, [memberId]);

  // Height only grows for kids — fetch the last two readings to compare
  // once we know both the member's age and that HEIGHT is actually tracked,
  // rather than firing on every profile view.
  useEffect(() => {
    if (
      !memberId ||
      !member ||
      !isChild(member) ||
      !vitalsTrackingConfig?.enabledParameters?.includes(VitalsParameter.HEIGHT)
    ) {
      setHeightIncreased(false);
      return;
    }
    let cancelled = false;
    vitalsTrackingService
      .getReadings(memberId, VitalsParameter.HEIGHT, 1, 2)
      .then(({ data }) => {
        if (cancelled || data.length < 2) return;
        const [latest, previous] = [...data].sort(
          (a, b) =>
            new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
        );
        setHeightIncreased(
          (latest.values.value ?? 0) > (previous.values.value ?? 0)
        );
      })
      .catch(() => {
        if (!cancelled) setHeightIncreased(false);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, member, vitalsTrackingConfig]);

  // Weight trend applies to everyone, adult or child — unlike height it can
  // go either way, so no age gate here.
  useEffect(() => {
    if (
      !memberId ||
      !vitalsTrackingConfig?.enabledParameters?.includes(VitalsParameter.WEIGHT)
    ) {
      setWeightTrend(null);
      return;
    }
    let cancelled = false;
    vitalsTrackingService
      .getReadings(memberId, VitalsParameter.WEIGHT, 1, 2)
      .then(({ data }) => {
        if (cancelled || data.length < 2) return;
        const [latest, previous] = [...data].sort(
          (a, b) =>
            new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
        );
        const latestValue = latest.values.value ?? 0;
        const previousValue = previous.values.value ?? 0;
        setWeightTrend(
          latestValue > previousValue
            ? "up"
            : latestValue < previousValue
            ? "down"
            : null
        );
      })
      .catch(() => {
        if (!cancelled) setWeightTrend(null);
      });
    return () => {
      cancelled = true;
    };
  }, [memberId, vitalsTrackingConfig]);

  // Data for the new card sections: lab trends, pillbox, reminders, records.
  // Tracked via allSettled so the cards can reveal together once every
  // section's data has arrived, instead of popping in one at a time.
  const [cardsDataLoaded, setCardsDataLoaded] = useState(false);

  // Restores scroll position on return from a quick-action tile (Reminders,
  // Records, Pillbox, etc. further down the page) instead of always landing
  // back at the top — same pattern as PillboxLanding's own useScrollRestore.
  // Gated on the same "content actually rendered" check the skeleton below
  // uses, so it doesn't try to scroll before there's anything to scroll to.
  const { saveScroll } = useScrollRestore(
    `memberDetail_${memberId}`,
    !member || !cardsDataLoaded
  );

  useEffect(() => {
    if (!memberId) return;
    setCardsDataLoaded(false);

    const labPromise = labInsightsService
      .getLanding(memberId)
      .then(setLabLanding)
      .catch(() => setLabLanding(null));

    const pillboxPromise = pillboxService
      .getLedger(memberId, "ACTIVE")
      .then(setPillboxItems)
      .catch(() => setPillboxItems([]));

    const remindersPromise = remindersService
      .getReminderTypeCounts(memberId)
      .then(setReminderCounts)
      .catch(() => setReminderCounts(null));

    const recordsPromise = getDocumentTypeCounts()
      .then((counts: any) => setRecordCounts(counts))
      .catch(() => setRecordCounts(null));

    Promise.allSettled([
      labPromise,
      pillboxPromise,
      remindersPromise,
      recordsPromise,
    ]).then(() => setCardsDataLoaded(true));
    // getDocumentTypeCounts isn't memoized by useDocument — depend on
    // memberId only, since the function is recreated every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId]);

  // Maatra Summary — latest PRESCRIPTION_HEALTH_SUMMARY actionable insight,
  // same source as the Health Summary page's Health Snapshot section.
  useEffect(() => {
    if (!memberId) {
      setMaatraSummary(null);
      setMaatraSummaryUpdatedAt(null);
      return;
    }
    actionableInsightsService
      .getHealthSummary(memberId, "PRESCRIPTION_HEALTH_SUMMARY")
      .then((res) => {
        const insights = res?.data ?? [];
        const latest = [...insights].sort((a, b) => {
          const aDate = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
          const bDate = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
          return bDate - aDate;
        })[0];
        if (!latest) {
          setMaatraSummary(null);
          setMaatraSummaryUpdatedAt(null);
          return;
        }
        const summary = latest.summary as any;
        const text =
          typeof summary === "string"
            ? summary
            : summary?.healthSummary || summary?.evidenceSynthesis || null;
        setMaatraSummary(text || null);
        setMaatraSummaryUpdatedAt(latest.updatedAt || latest.createdAt || null);
      })
      .catch(() => {
        setMaatraSummary(null);
        setMaatraSummaryUpdatedAt(null);
      });
  }, [memberId]);

  // Auto-open ProfileTrackerCard when returning from health profile pages
  useEffect(() => {
    const reopenTracker = searchParams.get("reopenTracker");

    if (reopenTracker === "true") {
      setShowProfileTracker(true);
      // Remove the query parameter to prevent reopening on subsequent renders
      searchParams.delete("reopenTracker");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Lazy load ProfileTracker data when it opens (skip document counts for STRONG profiles)
  useEffect(() => {
    if (showProfileTracker && !profileTrackerDataLoaded && memberId) {
      const isStrongProfile =
        memberStats?.profileStrength === ProfileStrength.STRONG;

      // Fetch healthProfileSummary and vitals
      import("@/services/health-profile.service").then(
        ({ healthProfileService }) => {
          healthProfileService
            .getProfileSummary(memberId)
            .then((summary) => {
              setHealthProfileSummary(summary);
            })
            .catch((err) =>
              console.error("Failed to load profile summary:", err)
            );
        }
      );

      import("@/services/vitals.service").then((VitalsServiceModule) => {
        VitalsServiceModule.default
          .getVitals(memberId)
          .then((vitalsData) => {
            setVitals(vitalsData);
          })
          .catch((err) => console.error("Failed to load vitals:", err));
      });

      // Fetch emergency contact info
      import("@/services/emergency-info.service").then(
        ({ emergencyInfoService }) => {
          emergencyInfoService
            .getEmergencyInfo(memberId)
            .then((data) => {
              setHasEmergencyContact(
                !!(data?.family_contact?.name && data?.family_contact?.phone)
              );
            })
            .catch((err) => {
              console.error("Failed to load emergency contact:", err);
              setHasEmergencyContact(false);
            });
        }
      );

      // Only fetch document type counts for LOW/MODERATE profiles
      if (!isStrongProfile && memberStats?.docsCount > 0) {
        getDocumentTypeCounts({ mode: "docsOnly" })
          .then((counts: any) => {
            if (counts) {
              setDocumentTypeCounts({
                prescription: counts.prescriptions || 0,
                labReport: counts.labReports || 0,
                dischargeSummary: counts.dischargeSummaries || 0,
                imagingReport: counts.imagingReports || 0,
                insuranceCard: counts.insurances || 0,
                medicalBill: counts.medicalReceipts || 0,
              });
            }
          })
          .catch((err) =>
            console.error("Failed to load document counts:", err)
          );
      }

      // Only fetch all documents for year span calculation for MODERATE profiles
      const isModerateProfile =
        memberStats?.profileStrength === ProfileStrength.MODERATE;
      if (isModerateProfile && memberStats?.docsCount > 0) {
        getAllDocuments({
          member_id: memberId,
          page: 1,
          pageSize: 100,
        })
          .then((response: any) => {
            if (response?.data && Array.isArray(response.data)) {
              const years = new Set<number>();
              response.data.forEach((doc: any) => {
                if (doc.documentDate) {
                  const year = new Date(doc.documentDate).getFullYear();
                  if (!Number.isNaN(year)) {
                    years.add(year);
                  }
                }
              });
              if (years.size > 0) {
                const sortedYears = Array.from(years).sort((a, b) => a - b);
                const span =
                  sortedYears[sortedYears.length - 1] - sortedYears[0] + 1;
                setDocumentYearSpan(span);
              } else {
                // No document dates found - set to 0 years
                setDocumentYearSpan(0);
              }
            }
          })
          .catch((err) =>
            console.error("Failed to load document year span:", err)
          );
      }

      setProfileTrackerDataLoaded(true);
    }
  }, [
    showProfileTracker,
    profileTrackerDataLoaded,
    memberId,
    memberStats?.profileStrength,
    memberStats?.docsCount,
    getDocumentTypeCounts,
    getAllDocuments,
  ]);

  const handleManageAccessibleBy = () => {
    saveScroll();
    // Navigate to the unified manage-access screen, opened on the "Who
    // sees you" tab (who can access the selectedMember's profile).
    navigate("/profile/family/manage-access/access-request", {
      state: {
        selectedMember: member,
        backUrl: window.location.pathname,
        initialTab: "seeing",
      },
    });
  };

  const handleManageCanAccess = () => {
    saveScroll();
    // Navigate to the unified manage-access screen, opened on the
    // "Profiles you see" tab (profiles the selectedMember can access).
    navigate("/profile/family/manage-access/member-can-access", {
      state: {
        selectedMember: member,
        backUrl: window.location.pathname,
        initialTab: "seen",
      },
    });
  };

  const handleEdit = () => {
    navigate(`/profile/family/edit/${memberId}`);
  };

  const handleSetSelectedMember = () => {
    // Update selectedMember in Redux
    if (member) {
      if (
        member.id === loggedInMember?.id ||
        member._id === loggedInMember?.id
      ) {
        dispatch(clearSelectedMember());
      } else {
        dispatch(setSelectedMember(member));
      }
    }
  };

  const handleDocumentsClick = () => {
    saveScroll();
    handleSetSelectedMember();
    const count = memberStats?.docsCount ?? 0;
    if (count === 0) {
      // Show upload options if no documents
      setShowUploadOptions(true);
    } else {
      // Navigate to documents navigation
      navigate("/documents", {
        state: { backUrl: window.location.pathname },
      });
    }
  };

  const handleRemindersClick = () => {
    saveScroll();
    handleSetSelectedMember();
    const count = memberStats?.remindersCount ?? 0;
    if (count === 0) {
      // Navigate to add reminder
      navigate("/reminders", {
        state: {
          memberId: member?.id || member?._id,
          backUrl: window.location.pathname,
        },
      });
    } else {
      // Navigate to reminders list
      navigate("/reminders", {
        state: {
          memberId: member?.id || member?._id,
          backUrl: window.location.pathname,
        },
      });
    }
  };

  const handleEmergencyCardClick = () => {
    saveScroll();
    handleSetSelectedMember();
    const isReady = memberStats?.emergencyCardReady ?? false;

    if (isReady) {
      navigate("/dashboard/emergency-card", {
        state: {
          memberId: member?.id || member?._id,
          backUrl: window.location.pathname,
        },
      });
    } else {
      navigate("/profile/emergency-card/activate", {
        state: {
          memberId: member?.id || member?._id,
          backUrl: window.location.pathname,
        },
      });
    }
  };

  const handleHealthProfileClick = () => {
    saveScroll();
    handleSetSelectedMember();
    navigate("/profile/emergency-card/activate", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleHealthSummaryClick = () => {
    saveScroll();
    handleSetSelectedMember();
    const isReady = memberStats?.healthSummaryReady ?? false;
    if (isReady) {
      // Navigate to health summary
      navigate("/health-summary", {
        state: {
          memberId: member?.id || member?._id,
          backUrl: window.location.pathname,
        },
      });
    } else {
      // Show dialog first
      setShowHealthSummaryDialog(true);
    }
  };

  const handleVitalsClick = () => {
    saveScroll();
    handleSetSelectedMember();
    navigate("/vitals-tracker", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleAddDocumentsFromHealthSummary = () => {
    setShowHealthSummaryDialog(false);
    // Show upload options
    setShowUploadOptions(true);
  };

  const handleLabInsightsClick = () => {
    saveScroll();
    handleSetSelectedMember();
    navigate("/lab-insights", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handlePillboxClick = () => {
    saveScroll();
    handleSetSelectedMember();
    navigate("/pillbox", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleSosClick = () => {
    saveScroll();
    handleSetSelectedMember();
    navigate("/pillbox/sos", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleMaatraSummaryClick = () => {
    saveScroll();
    navigate("/health-summary", {
      state: {
        memberId: member?.id || member?._id,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleContactClick = () => {
    setShowContactDialog(true);
  };

  const handleCopyToClipboard = async (type: "phone" | "email") => {
    const value =
      type === "phone"
        ? (member as any)?.mobile || (member as any)?.phone || ""
        : (member as any)?.email || "";

    if (value) {
      try {
        await navigator.clipboard.writeText(value);

        // Set copied state
        if (type === "phone") {
          setCopiedPhone(true);
          setTimeout(() => setCopiedPhone(false), 2000);
        } else {
          setCopiedEmail(true);
          setTimeout(() => setCopiedEmail(false), 2000);
        }
      } catch (error) {
        showToast({
          message: "Failed to copy to clipboard",
          variant: "error",
        });
      }
    }
  };

  const handleEditContact = () => {
    setShowContactDialog(false);
    navigate("/profile/basic-information", {
      state: { backUrl: window.location.pathname },
    });
  };

  const handleProfileTrackerNavigate = (path: string) => {
    // Add query parameter to current URL so we know to reopen ProfileTrackerCard on back navigation
    const currentPath = window.location.pathname;
    const pathWithParam = `${currentPath}?reopenTracker=true`;

    // Update navigation source to include reopenTracker parameter
    // This ensures modules that use useBackNavigation hook also return correctly
    setNavigationSource("member-detail", pathWithParam);

    navigate(path, { state: { backUrl: pathWithParam } });
  };

  const handleEditNickname = () => {
    setEditNicknameMember(member);
  };

  const handleSaveNickname = async (nickname: string) => {
    if (!editNicknameMember) return;
    setEditNicknameLoading(true);
    try {
      await memberPermissionService.updateNickname({
        memberId: loggedInMember.id,
        familyId: memberInfo?.current_family,
        familyMemberId: editNicknameMember.id || editNicknameMember._id,
        nickname,
      });
      setEditNicknameMember(null);
      showToast({
        message: "Nickname updated successfully",
        variant: "success",
      });
      // Refresh the page to show updated nickname
      window.location.reload();
    } catch (e) {
      console.error("Failed to update nickname:", e);
      showToast({
        message: "Failed to update nickname",
        variant: "error",
      });
    } finally {
      setEditNicknameLoading(false);
    }
  };

  const handleExitFamily = () => {
    const isSelf =
      (member?.id || member?._id) ===
      (loggedInMember?.id || loggedInMember?._id);
    const isFO = loggedInMember?.family_member_type === "fo";
    let eligibleForOwnership = [];
    if (isSelf && isFO && Array.isArray(familyMembers)) {
      eligibleForOwnership = familyMembers.filter(
        (m: any) =>
          m.registered_member &&
          (m.id || m._id) !== (loggedInMember?.id || loggedInMember?._id) &&
          m.family_member_type !== "fo"
      );
    }
    // Normalize member data for DeleteProfile compatibility
    const normalizedMember = {
      ...member,
      firstName: member?.first_name,
      lastName: member?.last_name,
    };
    navigate("/profile/family/delete-member", {
      state: {
        action: isSelf ? "exit" : "remove",
        targetMember: normalizedMember,
        eligibleForOwnership,
      },
    });
  };

  const handleDeleteProfile = () => {
    const isSelf =
      (member?.id || member?._id) ===
      (loggedInMember?.id || loggedInMember?._id);
    const registeredMembersCount = registeredMembers.length > 1;
    let eligibleForOwnership = [];
    if (
      isSelf &&
      loggedInMember?.family_member_type === "fo" &&
      Array.isArray(familyMembers)
    ) {
      eligibleForOwnership = familyMembers.filter(
        (m: any) =>
          m.registered_member &&
          (m.id || m._id) !== (loggedInMember?.id || loggedInMember?._id) &&
          m.family_member_type !== "fo"
      );
    }
    // Normalize member data for DeleteProfile compatibility
    const normalizedMember = {
      ...member,
      firstName: member?.first_name,
      lastName: member?.last_name,
    };
    navigate("/profile/family/delete-member", {
      state: {
        action: isSelf
          ? registeredMembersCount
            ? "fo-delete-registered"
            : "delete"
          : "delete-other",
        targetMember: normalizedMember,
        eligibleForOwnership,
      },
    });
  };

  const handleRemoveFromFamily = () => {
    // Normalize member data for DeleteProfile compatibility
    const normalizedMember = {
      ...member,
      firstName: member?.first_name,
      lastName: member?.last_name,
    };
    navigate("/profile/family/delete-member", {
      state: {
        action: "remove",
        targetMember: normalizedMember,
      },
    });
  };

  const handleInviteMember = () => {
    navigate("/profile/family/invite-contact-method", {
      state: {
        targetMember: member,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleResendInvite = () => {
    setManageInviteMenuOpen(false);
    navigate("/profile/family/invite-contact-method", {
      state: {
        targetMember: member,
        backUrl: window.location.pathname,
      },
    });
  };

  const handleCancelInvite = async () => {
    if (!memberId) return;
    setCancelInviteLoading(true);
    try {
      await membersService.revokeInvite(memberId);
      setShowCancelInviteDialog(false);
      // Reload so the family list reflects the updated registration_status
      window.location.reload();
    } catch {
      showToast({ message: t("errors.toast"), variant: "error" });
    } finally {
      setCancelInviteLoading(false);
    }
  };

  // Close manage-invite dropdown on outside click
  React.useEffect(() => {
    if (!manageInviteMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        manageInviteMenuRef.current &&
        !manageInviteMenuRef.current.contains(e.target as Node) &&
        manageInviteButtonRef.current &&
        !manageInviteButtonRef.current.contains(e.target as Node)
      ) {
        setManageInviteMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [manageInviteMenuOpen]);

  if (!member || !cardsDataLoaded) {
    return <MemberDetailSkeleton />;
  }

  // Member name: first + last name
  const memberName =
    `${member.first_name || ""}${
      member.last_name ? " " + member.last_name : ""
    }`.trim() || "Member";
  const memberAvatar = member.avatar;
  const isOwner = member.family_member_type === "fo";
  const isRegistered = member.registered_member;
  const isPet = member.member_type === MemberType.PET;
  const isMemberChild = isChild({
    dob_year: member.dob_year,
    dob_month: member.dob_month,
    dob_day: member.dob_day,
  });
  // Pets/children can't be granted access to manage other members' profiles.
  const canManageOthers = !isPet && !isMemberChild;

  // Nickname display - show "You" for logged-in member instead of nickname
  const isLoggedInMember =
    (member.id || member._id) === (loggedInMember?.id || loggedInMember?._id);
  const memberFirstName = member.nickname || member.first_name || "Member";
  const nicknameDisplay = isLoggedInMember
    ? "You"
    : member.nickname || member.first_name;

  // Calculate age
  let ageDisplay = "";
  if (member.dob_month && member.dob_year) {
    const now = new Date();
    let age = now.getFullYear() - member.dob_year;
    if (now.getMonth() + 1 < member.dob_month) {
      age--;
    }
    ageDisplay = `${age} yrs`;
  }

  // Extract weight from vitals
  let weightDisplay = "";
  if (vitals?.vitals && Array.isArray(vitals.vitals)) {
    const weightVital = vitals.vitals.find(
      (v: any) => v.name.toLowerCase() === "weight"
    );
    if (weightVital?.value) {
      const unit = weightVital.unit || "kg";
      weightDisplay = `${weightVital.value} ${unit}`;
    }
  }

  return (
    <div className={styles.container}>
      {/* Member Info Section - Horizontal Layout */}
      <div className={styles.memberInfoHorizontal}>
        <div className={styles.leftSection}>
          <div
            className={styles.avatarSection}
            onClick={() =>
              navigate("/profile/basic-information", {
                state: {
                  memberId: member?.id || member?._id,
                  backUrl: window.location.pathname,
                },
              })
            }
            style={{ cursor: "pointer" }}
          >
            <MAvatar
              size="xl"
              type={memberAvatar ? "dp" : "no-dp"}
              src={memberAvatar}
              text={`${member.first_name || ""}${
                member.last_name ? " " + member.last_name : ""
              }`.trim()}
              badgeIcon={isOwner ? <IconUserStar size={20} /> : undefined}
              className={
                isRegistered || isOwner
                  ? styles.avatarRegistered
                  : styles.avatarUnregistered
              }
            />
            {isRegistered && !isOwner && (
              <div className={styles.checkmarkBadge}>
                <MaatraLogo size={12} fill="#FBFBFB" />
              </div>
            )}
          </div>
          {isOwner && (
            <MBadge className={styles.ownerBadgeLabelDetail}>Owner</MBadge>
          )}
          {isRegistered && !isOwner && (
            <MBadge variant="purple-inverse">Maatra account</MBadge>
          )}
          {!isRegistered && member.registration_status === "invited" && (
            <MBadge variant="orange">Invited</MBadge>
          )}
          {isMemberChild && member.member_type !== "p" && (
            <MBadge variant="purple-inverse">Child</MBadge>
          )}
          {isPet && <MBadge variant="purple-inverse">Pet</MBadge>}
        </div>

        <div className={styles.rightSection}>
          <h1 className={styles.memberName}>{memberName}</h1>
          <div className={styles.memberMetaRow}>
            <div className={styles.memberMeta}>
              {nicknameDisplay && (
                <span className={styles.memberMetaNickname}>
                  {nicknameDisplay}
                </span>
              )}
              {nicknameDisplay && ageDisplay && (
                <span className={styles.memberMetaDot}>&bull;</span>
              )}
              {ageDisplay && (
                <span className={styles.memberMetaAge}>{ageDisplay}</span>
              )}
              {/* {weightDisplay && <span>{weightDisplay}</span>} */}
            </div>

            {/* Contact Icons - Hide for pets and unregistered members */}
            {!isPet && isRegistered && (
              <div className={styles.contactIcons}>
                <div
                  className={styles.contactIcon}
                  onClick={handleContactClick}
                >
                  <IconPhone size={16} color="#8764BB" />
                </div>
                <div className={styles.contactSeparator}></div>
                <div
                  className={styles.contactIcon}
                  onClick={handleContactClick}
                >
                  <IconMail size={16} color="#8764BB" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className={styles.quickActions}>
        <div
          className={styles.quickActionItem}
          onClick={handleEmergencyCardClick}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.quickActionIcon}>
            <IconMedicalCross size={32} color="#8764BB" />
          </div>
          <span className={styles.quickActionLabel}>
            Emergency
            <br aria-hidden />
            card
          </span>
          {!(
            memberStats?.emergencyCardComplete ||
            memberStats?.emergencyCardReady
          ) && <MBadge variant="yellow">Not ready</MBadge>}
        </div>

        <div
          className={styles.quickActionItem}
          onClick={handleHealthSummaryClick}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.quickActionIcon}>
            <IconClipboardPlus size={32} color="#8764BB" />
          </div>
          <span className={styles.quickActionLabel}>
            Health
            <br aria-hidden />
            summary
          </span>
          {!memberStats?.healthSummaryReady && (
            <MBadge variant="yellow">Not ready</MBadge>
          )}
        </div>

        <div
          className={styles.quickActionItem}
          onClick={handleHealthProfileClick}
          style={{ cursor: "pointer" }}
        >
          <div className={styles.quickActionIcon}>
            <IconClipboardList size={32} color="#8764BB" />
          </div>
          <span className={styles.quickActionLabel}>
            Health
            <br aria-hidden />
            Profile
          </span>
          {!memberStats?.healthProfileComplete && (
            <MBadge variant="yellow">Incomplete</MBadge>
          )}
        </div>
      </div>

      <div className={styles.memberDetailCards}>
        {/* Maatra Summary */}
        {/* {maatraSummary && (
          <MRevealSection delay={0}>
            <MaatraSummaryCard
              summary={maatraSummary}
              updatedAt={maatraSummaryUpdatedAt}
              onClick={handleMaatraSummaryClick}
            />
          </MRevealSection>
        )} */}

        {/* Lab trends */}
        <MRevealSection delay={0.05}>
          <LabTrendsCard
            landing={labLanding}
            onClick={handleLabInsightsClick}
            onEmptyClick={() => setShowUploadOptions(true)}
          />
        </MRevealSection>

        {/* Vitals tracker — hidden for pets */}
        {!isPet && (
          <MRevealSection delay={0.1}>
            <VitalsTrackerCard
              config={vitalsTrackingConfig}
              heightIncreased={heightIncreased}
              weightTrend={weightTrend}
              onClick={handleVitalsClick}
            />
          </MRevealSection>
        )}

        {/* Records added */}
        <MRevealSection delay={0.15}>
          <RecordsHeroCard
            counts={recordCounts}
            onClick={handleDocumentsClick}
          />
        </MRevealSection>

        {/* Pillbox */}
        <MRevealSection delay={0.2}>
          <PillboxCard
            items={pillboxItems}
            memberDisplayName={nicknameDisplay || memberName}
            isSelf={isLoggedInMember}
            onClick={handlePillboxClick}
            onSosClick={handleSosClick}
          />
        </MRevealSection>

        {/* Reminders */}
        <MRevealSection delay={0.25}>
          <RemindersCard
            counts={reminderCounts}
            onClick={handleRemindersClick}
          />
        </MRevealSection>
      </div>

      {/* Profile Access Section */}
      <MRevealSection delay={0.3} className={styles.profileAccessSection}>
        <div className={styles.profileAccessHeader}>
          <h2 className={styles.sectionTitle}>Profile access</h2>
          <button
            type="button"
            className={styles.profileAccessChevron}
            onClick={handleManageAccessibleBy}
            aria-label="Manage profile access"
          >
            <IconChevronRight size={16} />
          </button>
        </div>

        {(canManageOthers || accessibleByMembers.length > 0) && (
          <div className={styles.accessCard}>
            {/* Row 1: Which profiles this member can access — pets/children
                can't manage anyone else's, so this row doesn't apply to
                them. For everyone else it always shows, even at 0 (Figma's
                own example renders a "can see no profile yet" zero-state
                rather than hiding the row — previously this row vanished
                whenever the count was 0, which was "only one row shows"). */}
            {canManageOthers && (
              <div
                className={styles.accessRow}
                onClick={() => {
                  if (member?.registered_member) handleManageCanAccess();
                }}
                style={{
                  cursor: member?.registered_member ? "pointer" : "default",
                }}
              >
                <div className={styles.accessRowLeft}>
                  <div className={styles.accessRowIcon}>
                    <IconUser size={17} />
                  </div>
                  <span className={styles.accessRowText}>
                    {isLoggedInMember ? "You" : memberFirstName} can see{" "}
                    {managedMembers.length === 0 ? (
                      "no profile yet"
                    ) : (
                      <>
                        <strong>{managedMembers.length}</strong>{" "}
                        {managedMembers.length === 1 ? "profile" : "profiles"}
                      </>
                    )}
                  </span>
                </div>
                <div className={styles.accessRowAvatars}>
                  {managedMembers.slice(0, 2).map((managedMember: any) => (
                    <MAvatar
                      key={managedMember.id || managedMember._id}
                      type={managedMember.avatar ? "dp" : "no-dp"}
                      src={managedMember.avatar}
                      text={`${managedMember.first_name || ""}${
                        managedMember.last_name
                          ? " " + managedMember.last_name
                          : ""
                      }`.trim()}
                      px={32}
                      textSize={10.667}
                      className={styles.accessAvatar}
                    />
                  ))}
                  {managedMembers.length > 2 && (
                    <span className={styles.accessAvatarMore}>
                      +{managedMembers.length - 2}
                    </span>
                  )}
                </div>
              </div>
            )}

            {canManageOthers && <div className={styles.accessDivider} />}

            {/* Row 2: Who can access this member's profile — always shown
                whenever the card shows at all. */}
            <div
              className={styles.accessRow}
              onClick={handleManageAccessibleBy}
              style={{ cursor: "pointer" }}
            >
              <div className={styles.accessRowLeft}>
                <div className={styles.accessRowIcon}>
                  <IconUsers size={17} />
                </div>
                <span className={styles.accessRowText}>
                  {accessibleByMembers.length === 0 ? (
                    isLoggedInMember ? (
                      "No one can see you yet"
                    ) : (
                      <>No one can see {memberFirstName}&rsquo;s profile yet</>
                    )
                  ) : (
                    <>
                      <strong>{accessibleByMembers.length}</strong>{" "}
                      {accessibleByMembers.length === 1 ? "person" : "people"}{" "}
                      can see{" "}
                      {isLoggedInMember ? (
                        "you"
                      ) : (
                        <>{memberFirstName}&rsquo;s profile</>
                      )}
                    </>
                  )}
                </span>
              </div>
              <div className={styles.accessRowAvatars}>
                {accessibleByMembers.slice(0, 2).map((accessMember: any) => (
                  <MAvatar
                    key={accessMember.id || accessMember._id}
                    type={accessMember.avatar ? "dp" : "no-dp"}
                    src={accessMember.avatar}
                    text={`${accessMember.first_name || ""}${
                      accessMember.last_name ? " " + accessMember.last_name : ""
                    }`.trim()}
                    px={32}
                    textSize={10.667}
                    className={styles.accessAvatar}
                  />
                ))}
                {accessibleByMembers.length > 2 && (
                  <span className={styles.accessAvatarMore}>
                    +{accessibleByMembers.length - 2}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </MRevealSection>

      {/* Document Upload Options Drawer */}
      <MDocumentUploadOptions
        ref={uploadOptionsRef}
        open={showUploadOptions}
        onClose={() => setShowUploadOptions(false)}
      />

      {/* Health Summary Dialog */}
      <MDialog
        isOpen={showHealthSummaryDialog}
        onClose={() => setShowHealthSummaryDialog(false)}
        title="Health summary"
        description="Add 5 health documents, including 2 prescriptions, to unlock your Health Summary."
        primaryCtaText="Add documents"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleAddDocumentsFromHealthSummary}
        onSecondaryCta={() => setShowHealthSummaryDialog(false)}
      />

      {/* Contact Details Dialog */}
      <MDialog
        title="Contact Information"
        isOpen={showContactDialog}
        onClose={() => setShowContactDialog(false)}
      >
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            flexDirection: "column",
            gap: "24px",
          }}
        >
          {/* Mobile Number */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <MInputField
                variant="SummaryDisplay"
                size="md"
                label="Mobile number"
                showLabel={true}
                placeholder="Not Answered"
                value={(member as any)?.mobile || (member as any)?.phone || ""}
                readOnly
              />
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
              {/* When a value exists, only the copy icon is shown — edit is
                  redundant because the user can't change a populated contact
                  field from this modal. When the field is empty, only the
                  edit icon is shown so the user can add a value. */}
              {(member as any)?.mobile || (member as any)?.phone ? (
                <button
                  onClick={() => handleCopyToClipboard("phone")}
                  className={styles.contactIconButton}
                >
                  <IconCopy
                    size={20}
                    color={copiedPhone ? "#2A2A2A" : "#8764BB"}
                  />
                </button>
              ) : (
                <button
                  onClick={handleEditContact}
                  className={styles.contactIconButton}
                >
                  <IconEdit size={20} color="#8764BB" />
                </button>
              )}
            </div>
          </div>

          {/* Email */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <div style={{ flex: 1 }}>
              <MInputField
                variant="SummaryDisplay"
                size="md"
                label="Email"
                showLabel={true}
                placeholder="Not Answered"
                value={(member as any)?.email || ""}
                readOnly
              />
            </div>
            <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
              {(member as any)?.email ? (
                <button
                  onClick={() => handleCopyToClipboard("email")}
                  className={styles.contactIconButton}
                >
                  <IconCopy
                    size={20}
                    color={copiedEmail ? "#2A2A2A" : "#8764BB"}
                  />
                </button>
              ) : (
                <button
                  onClick={handleEditContact}
                  className={styles.contactIconButton}
                >
                  <IconEdit size={20} color="#8764BB" />
                </button>
              )}
            </div>
          </div>
        </div>
      </MDialog>

      {/* Profile Tracker Card */}
      <ProfileTrackerCard
        isOpen={showProfileTracker}
        onClose={() => setShowProfileTracker(false)}
        profileScore={memberStats?.profileScore || 0}
        profileStrength={
          (memberStats?.profileStrength as "low" | "moderate" | "strong") ||
          "low"
        }
        memberStats={memberStats}
        healthProfileSummary={healthProfileSummary}
        onNavigate={handleProfileTrackerNavigate}
        onUploadDocument={() => setShowUploadOptions(true)}
        hasEmergencyContact={hasEmergencyContact}
        memberType={member?.member_type === MemberType.PET ? "p" : "h"}
        loading={showProfileTracker && !profileTrackerDataLoaded}
        memberDob={{
          dob_year: member?.dob_year,
          dob_month: member?.dob_month,
          dob_day: member?.dob_day,
        }}
        documentTypeCounts={documentTypeCounts}
        documentYearSpan={documentYearSpan}
      />

      {/* Bottom CTAs */}
      {(() => {
        if (!member || !loggedInMember) {
          return null;
        }

        const isSelf =
          (member.id || member._id) ===
          (loggedInMember.id || loggedInMember._id);
        const isOwner = member.family_member_type === "fo";
        const loggedInOwner = loggedInMember.family_member_type === "fo";
        const registeredMembersCount = registeredMembers.length > 1;

        const hasManagedAccess = canManageMember(member.id || member._id);
        const isPetOrChild =
          member.member_type === MemberType.PET ||
          isChild({
            dob_year: member.dob_year,
            dob_month: member.dob_month,
            dob_day: member.dob_day,
          });
        const canShowInvite =
          member.registration_status === "not_invited" && !isPetOrChild;
        const canShowResend =
          member.registration_status === "invited" && !isPetOrChild;

        const menuOptions = [];

        // 1. Edit nickname - always first if available
        // Don't show Edit Nickname when FM is viewing their own profile
        if ((loggedInOwner && !isSelf) || (hasManagedAccess && !isSelf)) {
          menuOptions.push({
            id: "edit-nickname",
            label: "Edit nickname",
            icon: <IconEdit size={20} />,
            onClick: handleEditNickname,
          });
        }

        // 2. Invite/Resend actions - second priority
        // Available for FO and FMs who have access to the member
        const canManageInvites =
          (loggedInOwner || hasManagedAccess) &&
          !isSelf &&
          !member.registered_member;
        if (canManageInvites) {
          if (canShowInvite) {
            menuOptions.push({
              id: "invite",
              label: "Send invite",
              icon: <IconMail size={20} />,
              onClick: handleInviteMember,
            });
          } else if (canShowResend) {
            menuOptions.push({
              id: "manage-invite",
              label: t("memberDetail.manageInvite"),
              icon: <IconMail size={20} />,
              onClick: () => setManageInviteMenuOpen((v) => !v),
            });
          }
        }

        // 3. Delete/Remove actions - last priority
        if (loggedInOwner) {
          if (isSelf && member.registered_member) {
            // Show Leave Family only if there are other registered members
            if (registeredMembers.length > 1) {
              menuOptions.push({
                id: "exit",
                label: "Leave Family",
                icon: <IconLogout size={20} />,
                onClick: handleExitFamily,
              });
            }
            // Always show Delete Account for FO viewing their own profile
            menuOptions.push({
              id: "delete-profile",
              label: "Delete account",
              icon: <IconUserX size={20} />,
              onClick: handleDeleteProfile,
              className: "text-red-600",
            });
          } else if (!isSelf && member.registered_member) {
            menuOptions.push({
              id: "remove",
              label: "Remove member",
              icon: <IconUserX size={20} />,
              onClick: handleRemoveFromFamily,
            });
          } else if (!isSelf && !member.registered_member) {
            menuOptions.push({
              id: "delete-profile",
              label: "Delete member",
              icon: <IconUserX size={20} />,
              onClick: handleDeleteProfile,
            });
          }
        } else if (!isOwner) {
          // Family member viewing their own profile
          if (isSelf) {
            menuOptions.push({
              id: "exit",
              label: "Leave Family",
              icon: <IconLogout size={20} />,
              onClick: handleExitFamily,
            });
            menuOptions.push({
              id: "delete-profile",
              label: "Delete account",
              icon: <IconUserX size={20} />,
              onClick: handleDeleteProfile,
              className: "text-red-600",
            });
          }
        }

        if (menuOptions.length === 0) return null;

        // Organize buttons: first two in a row, rest full width
        const firstRowButtons = menuOptions.slice(0, 2);
        const remainingButtons = menuOptions.slice(2);

        const renderButton = (
          option: (typeof menuOptions)[number],
          fullWidth: boolean
        ) => {
          if (option.id === "manage-invite") {
            return (
              <div
                key="manage-invite"
                ref={manageInviteButtonRef}
                style={{
                  position: "relative",
                  flex: fullWidth ? "1 1 100%" : undefined,
                }}
              >
                <MButton
                  variant="outline"
                  size="md"
                  state="default"
                  leftIcon={option.icon}
                  onClick={() => setManageInviteMenuOpen((v) => !v)}
                  className={
                    fullWidth ? styles.ctaButtonFull : styles.ctaButton
                  }
                >
                  {option.label}
                </MButton>
                {manageInviteMenuOpen &&
                  ReactDOM.createPortal(
                    <div
                      ref={manageInviteMenuRef}
                      className="moptions-menu"
                      style={(() => {
                        const el = manageInviteButtonRef.current;
                        if (!el) return { display: "none" };
                        const rect = el.getBoundingClientRect();
                        return {
                          position: "fixed" as const,
                          top: "auto",
                          right: "auto",
                          bottom: window.innerHeight - rect.top + 8,
                          left: rect.left,
                          width: rect.width,
                          zIndex: 2000,
                        };
                      })()}
                    >
                      <button
                        className="moptions-menu__item"
                        onClick={handleResendInvite}
                      >
                        <span className="moptions-menu__icon">
                          <IconMail size={20} />
                        </span>
                        <span>{t("memberDetail.resendInvite")}</span>
                      </button>
                      <div className="moptions-menu__separator" />
                      <button
                        className="moptions-menu__item delete"
                        onClick={() => {
                          setManageInviteMenuOpen(false);
                          setShowCancelInviteDialog(true);
                        }}
                      >
                        <span className="moptions-menu__icon">
                          <IconX size={20} />
                        </span>
                        <span>{t("memberDetail.cancelInvite")}</span>
                      </button>
                    </div>,
                    document.body
                  )}
              </div>
            );
          }
          return (
            <MButton
              key={option.id}
              variant="outline"
              size="md"
              state="default"
              leftIcon={option.icon}
              onClick={option.onClick}
              className={fullWidth ? styles.ctaButtonFull : styles.ctaButton}
            >
              {option.label}
            </MButton>
          );
        };

        return (
          <div className={styles.ctaSection}>
            {firstRowButtons.length > 0 && (
              <div className={styles.ctaRow}>
                {firstRowButtons.map((option) =>
                  renderButton(option, firstRowButtons.length === 1)
                )}
              </div>
            )}
            {remainingButtons.map((option) => renderButton(option, true))}
          </div>
        );
      })()}

      {/* Cancel Invite Confirmation Dialog */}
      <MDialog
        isOpen={showCancelInviteDialog}
        onClose={() => setShowCancelInviteDialog(false)}
        title={t("memberDetail.cancelInviteDialog.title", {
          name: member?.first_name || "",
        })}
        description={t("memberDetail.cancelInviteDialog.description")}
        primaryCtaText={t("memberDetail.cancelInviteDialog.primaryCta")}
        secondaryCtaText={t("memberDetail.cancelInviteDialog.secondaryCta")}
        onPrimaryCta={handleCancelInvite}
        onSecondaryCta={() => setShowCancelInviteDialog(false)}
        isPrimaryCtaLoading={cancelInviteLoading}
      />

      {/* Edit Nickname Drawer */}
      <EditNicknameDrawer
        open={!!editNicknameMember}
        member={editNicknameMember}
        onClose={() => setEditNicknameMember(null)}
        onDone={handleSaveNickname}
      />
    </div>
  );
};

export default MemberDetail;
